import {
  Box, Chip, CircularProgress, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';

import type { TravelMode, TripDay } from '../api/types';

/**
 * The trip, one card per day, as the way of moving between them.
 *
 * Tabs were the obvious answer and the wrong one: a day is not a label, it is
 * a date, a travel mode, a number of stops and — often enough to matter —
 * a failure. All of that has to be visible at once, because the question
 * somebody actually asks of a trip is "which day is the thin one?"
 */

const MODE_ICONS: Record<TravelMode, React.ReactNode> = {
  walk: <DirectionsWalkIcon sx={{ fontSize: 15 }} />,
  bike: <DirectionsBikeIcon sx={{ fontSize: 15 }} />,
  motorcycle: <TwoWheelerIcon sx={{ fontSize: 15 }} />,
  car: <DirectionsCarIcon sx={{ fontSize: 15 }} />,
  taxi: <LocalTaxiIcon sx={{ fontSize: 15 }} />,
  truck: <LocalShippingIcon sx={{ fontSize: 15 }} />,
  transit: <DirectionsTransitIcon sx={{ fontSize: 15 }} />,
};

/** "Mon 31 Aug" — enough to place the day without a full date. */
function dayLabel(date: string | null, index: number): { top: string; sub: string } {
  if (!date) return { top: `Day ${index + 1}`, sub: '' };
  const [y, m, d] = date.split('-').map(Number);
  // Built in UTC deliberately: the weekday of a calendar date does not depend
  // on where the browser happens to be.
  const when = new Date(Date.UTC(y!, m! - 1, d!));
  return {
    top: `Day ${index + 1}`,
    sub: when.toLocaleDateString('en-GB', {
      timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short',
    }),
  };
}

/** A day as the strip reads it: planned or not. */
export type StripDay = Pick<TripDay, 'index' | 'date' | 'skipped'>
  & Partial<Pick<TripDay, 'plan' | 'error'>>;

export function TripDayStrip({
  days,
  selected,
  onSelect,
  colourFor,
  stale,
  busyIndex,
  scope = 'day',
  onScopeChange,
}: {
  /*
    Only what a card actually shows, so days that have not been planned yet
    can be listed too — which is what lets the strip stand before anything
    has been planned, and the map be used to build the operation rather than
    only to adjust a finished one.
  */
  days: StripDay[];
  selected: number;
  onSelect: (index: number) => void;
  /**
   * Whether what is done on the map lands on the operation or on one day.
   *
   * A card at the head of the strip rather than a question in a dialog: the
   * form asks everything twice over — once for the whole operation, once per
   * day — and this is the same choice, so it belongs beside the days it is
   * an alternative to.
   */
  scope?: 'operation' | 'day';
  onScopeChange?: (next: 'operation' | 'day') => void;
  /** Same colour the map draws that day in, so the two agree. */
  colourFor: (index: number) => string;
  /** Days whose endpoint assumptions changed and have not been re-planned. */
  stale: number[];
  /** The day currently being re-planned, if any. */
  busyIndex: number | null;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ overflowX: 'auto', pb: 1, scrollSnapType: 'x mandatory' }}
    >
      {/*
        The operation itself, before its days.

        Deliberately not a day card: no colour bar, no stop count, nothing a
        day has, because it is not one. Picking it says that the next thing
        marked on the map is held in common — the day on screen stays drawn,
        since there is no single route for "every day" to show.
      */}
      {onScopeChange && (
        <Paper
          variant="outlined"
          onClick={() => onScopeChange(scope === 'operation' ? 'day' : 'operation')}
          sx={{
            px: 1.5, py: 1,
            minWidth: 120,
            flexShrink: 0,
            cursor: 'pointer',
            scrollSnapAlign: 'start',
            borderColor: scope === 'operation' ? 'primary.main' : 'divider',
            borderWidth: scope === 'operation' ? 2 : 1,
            bgcolor: scope === 'operation' ? 'action.selected' : 'background.paper',
            '&:hover': {
              borderColor: scope === 'operation' ? 'primary.main' : 'text.disabled',
            },
          }}
        >
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <PublicIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Common</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Every day
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {scope === 'operation' ? 'Marking here' : 'Mark for all'}
          </Typography>
        </Paper>
      )}

      {days.map((day) => {
        const { top, sub } = dayLabel(day.date, day.index);
        // Still the day being DRAWN while the operation holds the marking, so
        // the strip never stops saying which route is on screen.
        const active = day.index === selected && scope === 'day';
        const colour = colourFor(day.index);
        const stops = day.plan ? Math.max(0, day.plan.stops.length - 2) : 0;
        const isStale = stale.includes(day.index);

        return (
          <Paper
            key={day.index}
            variant="outlined"
            onClick={() => { onScopeChange?.('day'); onSelect(day.index); }}
            sx={{
              px: 1.5, py: 1,
              minWidth: 132,
              flexShrink: 0,
              cursor: 'pointer',
              scrollSnapAlign: 'start',
              position: 'relative',
              opacity: day.skipped ? 0.55 : 1,
              borderColor: active ? 'primary.main' : 'divider',
              borderWidth: active ? 2 : 1,
              bgcolor: active ? 'action.selected' : 'background.paper',
              '&:hover': { borderColor: active ? 'primary.main' : 'text.disabled' },
            }}
          >
            {/* The map's colour for this day, so the strip and the lines agree. */}
            <Box sx={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
              bgcolor: colour, borderRadius: '4px 0 0 4px',
            }} />

            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', pl: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{top}</Typography>
              {day.plan && (
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                  {MODE_ICONS[day.plan.mode]}
                </Box>
              )}
              {busyIndex === day.index && <CircularProgress size={12} />}
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5 }}>
              {sub}
            </Typography>

            <Box sx={{ pl: 0.5, mt: 0.5 }}>
              {day.skipped ? (
                <Typography variant="caption" color="text.secondary">Not planned</Typography>
              ) : !day.plan && !day.error ? (
                <Typography variant="caption" color="text.secondary">Not planned yet</Typography>
              ) : day.error ? (
                <Tooltip title={day.error} arrow>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <ErrorOutlineIcon color="error" sx={{ fontSize: 14 }} />
                    <Typography variant="caption" color="error">Could not plan</Typography>
                  </Stack>
                </Tooltip>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {stops} {stops === 1 ? 'stop' : 'stops'}
                  {day.plan ? ` · ${(day.plan.totals.distanceMeters / 1000).toFixed(1)} km` : ''}
                </Typography>
              )}
            </Box>

            {/*
              A day is stale when an earlier one moved the endpoint it
              inherited. Marked rather than re-planned: re-running five days
              because somebody nudged the second is not a decision to make on
              their behalf.
            */}
            {isStale && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="Out of date"
                sx={{ mt: 0.5, ml: 0.5, height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}
